/**
 * 벌점 설정 탭
 */
import React, { useState, useEffect, useCallback } from 'react';
import SundayMeritSettings from './SundayMeritSettings';
import SundayAttendanceView from './SundayAttendanceView';
import LateReturnSettings from './LateReturnSettings';
import OutingOverdueSettings from './OutingOverdueSettings';

import { apiClient } from '../../../../services/authApi';
import {
  PenaltyType,
  PenaltyTypeCreate,
  PenaltyTypeUpdate,
  PenaltyThreshold,
  PenaltyThresholdCreate,
  PenaltyThresholdUpdate,
  PenaltySetting,
  PenaltySettingUpdate,
  THRESHOLD_COLOR_OPTIONS,
  RESET_PERIOD_OPTIONS,
  THRESHOLD_COLORS,
} from '../../models/Penalty';

interface PatrolPenaltyItem {
  id: string;
  name: string;
  emoji: string;
  points: number;
  point_type: 'demerit' | 'merit';
  color: string;
  display_order: number;
  is_active: boolean;
  notify_parent?: boolean;
}

const MAX_PATROL_ITEMS = 6;  // 2026-10-06 대표 결정: 5 → 6 (서버 MAX_ITEMS 와 같이)
const COLOR_OPTIONS = [
  { value: 'red', label: '빨강', class: 'bg-red-200' },
  { value: 'green', label: '초록', class: 'bg-green-200' },
  { value: 'gray', label: '회색', class: 'bg-gray-200' },
  { value: 'blue', label: '파랑', class: 'bg-blue-200' },
  { value: 'orange', label: '주황', class: 'bg-orange-200' },
];
const EMOJI_OPTIONS = ['😴', '📱', '💬', '✅', '📚', '🚫', '⏰', '🏆', '⚠️', '🔇'];

interface PenaltySettingsTabProps {
  types: PenaltyType[];
  thresholds: PenaltyThreshold[];
  setting: PenaltySetting | null;
  onCreateType: (data: Omit<PenaltyTypeCreate, 'organization_id'>) => Promise<void>;
  onUpdateType: (typeId: string, data: PenaltyTypeUpdate) => Promise<void>;
  onDeleteType: (typeId: string) => Promise<void>;
  onInitTypes: () => Promise<{ created_count: number; message: string }>;
  onCreateThreshold: (data: Omit<PenaltyThresholdCreate, 'organization_id'>) => Promise<void>;
  onUpdateThreshold: (thresholdId: string, data: PenaltyThresholdUpdate) => Promise<void>;
  onDeleteThreshold: (thresholdId: string) => Promise<void>;
  onUpdateSetting: (data: PenaltySettingUpdate) => Promise<void>;
  onResetPenalties: () => Promise<number>;
}

export const PenaltySettingsTab: React.FC<PenaltySettingsTabProps> = ({
  types,
  thresholds,
  setting,
  onCreateType,
  onUpdateType,
  onDeleteType,
  onInitTypes,
  onCreateThreshold,
  onUpdateThreshold,
  onDeleteThreshold,
  onUpdateSetting,
  onResetPenalties,
}) => {
  const [editingType, setEditingType] = useState<PenaltyType | null>(null);
  const [newType, setNewType] = useState<Partial<PenaltyTypeCreate> | null>(null);
  const [editingThreshold, setEditingThreshold] = useState<PenaltyThreshold | null>(null);
  const [newThreshold, setNewThreshold] = useState<Partial<PenaltyThresholdCreate> | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // 출석부 순찰 항목 관리
  const [patrolItems, setPatrolItems] = useState<PatrolPenaltyItem[]>([]);
  const [editingPatrolItem, setEditingPatrolItem] = useState<PatrolPenaltyItem | null>(null);
  const [newPatrolItem, setNewPatrolItem] = useState<Partial<PatrolPenaltyItem> | null>(null);
  const [patrolLoading, setPatrolLoading] = useState(false);

  const loadPatrolItems = useCallback(async () => {
    try {
      setPatrolLoading(true);
      const res = await apiClient.get('/v1/owner/patrol-penalty-items/all');
      setPatrolItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      // 실패 시 빈 배열
    } finally {
      setPatrolLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPatrolItems();
  }, [loadPatrolItems]);

  // 순찰 노트 화면의 [벌점 관리에서 변경] 으로 들어오면(#patrol-items) 순찰 항목까지 내려간다 (2026-10-06)
  useEffect(() => {
    if (patrolLoading || window.location.hash !== '#patrol-items') return;
    const el = document.getElementById('patrol-items');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [patrolLoading]);

  const handleCreatePatrolItem = async () => {
    if (!newPatrolItem?.name) return;
    try {
      await apiClient.post('/v1/owner/patrol-penalty-items', {
        name: newPatrolItem.name,
        emoji: newPatrolItem.emoji || '',
        points: newPatrolItem.points || 1,
        point_type: newPatrolItem.point_type || 'demerit',
        color: newPatrolItem.color || 'red',
        display_order: patrolItems.length,
        notify_parent: !!(newPatrolItem as any).notify_parent,
      });
      setNewPatrolItem(null);
      loadPatrolItems();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '항목 추가 실패');
    }
  };

  const handleUpdatePatrolItem = async () => {
    if (!editingPatrolItem) return;
    try {
      await apiClient.put(`/v1/owner/patrol-penalty-items/${editingPatrolItem.id}`, {
        name: editingPatrolItem.name,
        emoji: editingPatrolItem.emoji,
        points: editingPatrolItem.points,
        point_type: editingPatrolItem.point_type,
        color: editingPatrolItem.color,
        notify_parent: !!editingPatrolItem.notify_parent,
      });
      setEditingPatrolItem(null);
      loadPatrolItems();
    } catch {
      alert('항목 수정 실패');
    }
  };

  const handleDeletePatrolItem = async (id: string) => {
    if (!window.confirm('이 항목을 삭제하시겠습니까?')) return;
    try {
      await apiClient.delete(`/v1/owner/patrol-penalty-items/${id}`);
      loadPatrolItems();
    } catch {
      alert('항목 삭제 실패');
    }
  };

  // 기본 유형 초기화
  const handleInitTypes = async () => {
    if (isInitializing) return;
    setIsInitializing(true);
    try {
      const result = await onInitTypes();
      if (result.created_count > 0) {
        alert(result.message);
      } else {
        alert('이미 모든 기본 유형이 생성되어 있습니다.');
      }
    } catch (e) {
      alert('기본 유형 생성에 실패했습니다: ' + (e instanceof Error ? e.message : '알 수 없는 오류'));
    } finally {
      setIsInitializing(false);
    }
  };

  // 유형 저장
  const handleSaveType = async () => {
    if (newType) {
      if (!newType.name || !newType.code) return;
      await onCreateType({
        name: newType.name,
        code: newType.code,
        default_points: newType.default_points || 1,
        description: newType.description,
        point_type: newType.point_type || 'demerit',
      });
      setNewType(null);
    } else if (editingType) {
      await onUpdateType(editingType.id, {
        name: editingType.name,
        default_points: editingType.default_points,
        description: editingType.description,
        point_type: editingType.point_type,
        is_active: editingType.is_active,
      });
      setEditingType(null);
    }
  };

  // 임계값 저장
  const handleSaveThreshold = async () => {
    if (newThreshold) {
      if (!newThreshold.name || !newThreshold.points) return;
      await onCreateThreshold({
        name: newThreshold.name,
        points: newThreshold.points,
        color: newThreshold.color || 'yellow',
        description: newThreshold.description,
      });
      setNewThreshold(null);
    } else if (editingThreshold) {
      await onUpdateThreshold(editingThreshold.id, {
        name: editingThreshold.name,
        points: editingThreshold.points,
        color: editingThreshold.color,
        description: editingThreshold.description,
        is_active: editingThreshold.is_active,
      });
      setEditingThreshold(null);
    }
  };

  // 초기화 실행
  const handleReset = async () => {
    const count = await onResetPenalties();
    setShowResetConfirm(false);
    alert(`${count}명의 학생 벌점이 초기화되었습니다.`);
  };

  return (
    <div className="space-y-6">
      {/* 일요일 등원 상점 설정 */}
      <SundayMeritSettings />

      {/* 일요일 출석 정리 + 수동 부여 */}
      <SundayAttendanceView />

      {/* 벌점 유형 관리 */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-medium text-gray-900">벌점 유형 관리</h3>
          <div className="flex gap-2">
            <button
              onClick={handleInitTypes}
              disabled={isInitializing}
              className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              {isInitializing ? '생성 중...' : '기본 유형 생성'}
            </button>
            <button
              onClick={() =>
                setNewType({ name: '', code: '', default_points: 1, description: '', point_type: 'demerit' })
              }
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              유형 추가
            </button>
          </div>
        </div>

        <div className="p-4">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">이름</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">코드</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">기본 점수</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">자동</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">활성</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {/* 새 유형 입력 행 */}
              {newType && (
                <tr className="bg-blue-50">
                  <td className="px-3 py-2">
                    <input
                      value={newType.name || ''}
                      onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                      placeholder="유형 이름"
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      value={newType.code || ''}
                      onChange={(e) => setNewType({ ...newType, code: e.target.value })}
                      placeholder="코드 (영문)"
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <select
                        value={newType.point_type || 'demerit'}
                        onChange={(e) =>
                          setNewType({ ...newType, point_type: e.target.value as 'merit' | 'demerit' })
                        }
                        className="border rounded px-1 py-1 text-sm"
                      >
                        <option value="demerit">벌점(-)</option>
                        <option value="merit">상점(+)</option>
                      </select>
                      <input
                        type="number"
                        value={newType.default_points || 1}
                        onChange={(e) =>
                          setNewType({ ...newType, default_points: parseInt(e.target.value) || 1 })
                        }
                        className="w-14 border rounded px-2 py-1 text-sm text-center"
                        min={1}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">-</td>
                  <td className="px-3 py-2 text-center">-</td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={handleSaveType}
                      className="text-sm text-green-600 hover:text-green-700 mr-2"
                    >
                      저장
                    </button>
                    <button
                      onClick={() => setNewType(null)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      취소
                    </button>
                  </td>
                </tr>
              )}

              {types.map((type) => (
                <tr key={type.id} className={!type.is_active ? 'opacity-50' : ''}>
                  {editingType?.id === type.id ? (
                    <>
                      <td className="px-3 py-2">
                        <input
                          value={editingType.name}
                          onChange={(e) =>
                            setEditingType({ ...editingType, name: e.target.value })
                          }
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-500">{type.code}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <select
                            value={editingType.point_type || 'demerit'}
                            onChange={(e) =>
                              setEditingType({
                                ...editingType,
                                point_type: e.target.value as 'merit' | 'demerit',
                              })
                            }
                            className="border rounded px-1 py-1 text-sm"
                          >
                            <option value="demerit">벌점(-)</option>
                            <option value="merit">상점(+)</option>
                          </select>
                          <input
                            type="number"
                            value={editingType.default_points}
                            onChange={(e) =>
                              setEditingType({
                                ...editingType,
                                default_points: parseInt(e.target.value) || 1,
                              })
                            }
                            className="w-14 border rounded px-2 py-1 text-sm text-center"
                            min={1}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {type.is_auto ? '자동' : '-'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={editingType.is_active}
                          onChange={(e) =>
                            setEditingType({ ...editingType, is_active: e.target.checked })
                          }
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={handleSaveType}
                          className="text-sm text-green-600 hover:text-green-700 mr-2"
                        >
                          저장
                        </button>
                        <button
                          onClick={() => setEditingType(null)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          취소
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2">
                        <span className="font-medium">{type.name}</span>
                        {type.is_system && (
                          <span className="ml-2 text-xs text-gray-400">(시스템)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-sm">{type.code}</td>
                      <td className="px-3 py-2 text-center">
                        {type.point_type === 'merit' ? (
                          <span className="text-blue-600 font-medium">+{type.default_points}점 (상점)</span>
                        ) : (
                          <span className="text-red-600 font-medium">-{type.default_points}점 (벌점)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {type.is_auto && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            자동
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {type.is_active ? (
                          <span className="text-green-600">활성</span>
                        ) : (
                          <span className="text-gray-400">비활성</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => setEditingType(type)}
                          className="text-sm text-blue-600 hover:text-blue-700 mr-2"
                        >
                          수정
                        </button>
                        {!type.is_system && (
                          <button
                            onClick={() => onDeleteType(type.id)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            삭제
                          </button>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* 출석부 순찰 항목 관리 — 벌점 유형 바로 아래 배치 */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div id="patrol-items" className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">📋 출석부 순찰 항목 관리</h3>
            <p className="text-sm text-gray-500 mt-1">출석부 상세보기에서 학생에게 부여할 순찰/벌점 버튼 (최대 {MAX_PATROL_ITEMS}개)</p>
          </div>
          <span className="text-sm font-medium text-gray-500">
            {patrolItems.filter(i => i.is_active).length}/{MAX_PATROL_ITEMS}
          </span>
        </div>

        {patrolLoading ? (
          <div className="text-center py-8 text-gray-500">불러오는 중...</div>
        ) : (
          <>
            <div className="space-y-2 mb-4">
              {patrolItems.map((item) => (
                <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                  {editingPatrolItem?.id === item.id ? (
                    <>
                      <select value={editingPatrolItem.emoji} onChange={(e) => setEditingPatrolItem({...editingPatrolItem, emoji: e.target.value})} className="w-16 text-center text-xl border rounded p-1">
                        {EMOJI_OPTIONS.map(em => <option key={em} value={em}>{em}</option>)}
                      </select>
                      <input type="text" value={editingPatrolItem.name} onChange={(e) => setEditingPatrolItem({...editingPatrolItem, name: e.target.value})} className="flex-1 border rounded px-2 py-1 text-sm" maxLength={20} />
                      <select value={editingPatrolItem.point_type} onChange={(e) => setEditingPatrolItem({...editingPatrolItem, point_type: e.target.value as 'demerit' | 'merit'})} className="border rounded px-2 py-1 text-sm">
                        <option value="demerit">벌점</option>
                        <option value="merit">상점</option>
                      </select>
                      <input type="number" value={editingPatrolItem.points} onChange={(e) => setEditingPatrolItem({...editingPatrolItem, points: Math.max(0, parseInt(e.target.value) || 0)})} className="w-16 border rounded px-2 py-1 text-sm text-center" min={0} max={10} />
                      <span className="text-xs text-gray-400">점</span>
                      <select value={editingPatrolItem.color} onChange={(e) => setEditingPatrolItem({...editingPatrolItem, color: e.target.value})} className="border rounded px-2 py-1 text-sm">
                        {COLOR_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                        <input type="checkbox" checked={!!editingPatrolItem.notify_parent} onChange={(e) => setEditingPatrolItem({...editingPatrolItem, notify_parent: e.target.checked})} />
                        학부모 알림
                      </label>
                      <button onClick={handleUpdatePatrolItem} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">저장</button>
                      <button onClick={() => setEditingPatrolItem(null)} className="px-3 py-1 bg-gray-200 rounded text-sm hover:bg-gray-300">취소</button>
                    </>
                  ) : (
                    <>
                      <span className="text-xl w-8 text-center">{item.emoji}</span>
                      <span className="flex-1 font-medium text-sm">{item.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.point_type === 'merit' ? 'bg-green-100 text-green-700' : item.points === 0 ? 'bg-gray-100 text-gray-500' : 'bg-red-100 text-red-700'}`}>
                        {item.points === 0 ? '기록만' : item.point_type === 'merit' ? `상점 +${item.points}점` : `벌점 -${item.points}점`}
                      </span>
                      {item.notify_parent && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700" title="부여 시 학부모에게 알림톡 발송">📨 알림</span>
                      )}
                      <button onClick={() => setEditingPatrolItem({...item})} className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded">수정</button>
                      <button onClick={() => handleDeletePatrolItem(item.id)} className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded">삭제</button>
                    </>
                  )}
                </div>
              ))}
            </div>

            {newPatrolItem ? (
              <div className="flex items-center gap-3 p-3 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50">
                <select value={newPatrolItem.emoji || '😴'} onChange={(e) => setNewPatrolItem({...newPatrolItem, emoji: e.target.value})} className="w-16 text-center text-xl border rounded p-1">
                  {EMOJI_OPTIONS.map(em => <option key={em} value={em}>{em}</option>)}
                </select>
                <input type="text" value={newPatrolItem.name || ''} onChange={(e) => setNewPatrolItem({...newPatrolItem, name: e.target.value})} className="flex-1 border rounded px-2 py-1 text-sm" placeholder="항목 이름" maxLength={20} />
                <select value={newPatrolItem.point_type || 'demerit'} onChange={(e) => setNewPatrolItem({...newPatrolItem, point_type: e.target.value as 'demerit' | 'merit'})} className="border rounded px-2 py-1 text-sm">
                  <option value="demerit">벌점</option>
                  <option value="merit">상점</option>
                </select>
                <input type="number" value={newPatrolItem.points ?? 1} onChange={(e) => setNewPatrolItem({...newPatrolItem, points: Math.max(0, parseInt(e.target.value) || 0)})} className="w-16 border rounded px-2 py-1 text-sm text-center" min={0} max={10} />
                <span className="text-xs text-gray-400">점</span>
                <select value={newPatrolItem.color || 'red'} onChange={(e) => setNewPatrolItem({...newPatrolItem, color: e.target.value})} className="border rounded px-2 py-1 text-sm">
                  {COLOR_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                  <input type="checkbox" checked={!!(newPatrolItem as any).notify_parent} onChange={(e) => setNewPatrolItem({...newPatrolItem, notify_parent: e.target.checked} as any)} />
                  학부모 알림
                </label>
                <button onClick={handleCreatePatrolItem} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">추가</button>
                <button onClick={() => setNewPatrolItem(null)} className="px-3 py-1 bg-gray-200 rounded text-sm hover:bg-gray-300">취소</button>
              </div>
            ) : (
              <button
                onClick={() => setNewPatrolItem({ emoji: '😴', name: '', points: 1, point_type: 'demerit', color: 'red', notify_parent: false } as any)}
                disabled={patrolItems.filter(i => i.is_active).length >= MAX_PATROL_ITEMS}
                className={`w-full py-3 border-2 border-dashed rounded-lg text-sm font-medium ${patrolItems.filter(i => i.is_active).length >= MAX_PATROL_ITEMS ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
              >
                {patrolItems.filter(i => i.is_active).length >= MAX_PATROL_ITEMS ? `최대 ${MAX_PATROL_ITEMS}개까지 등록 가능합니다` : '+ 새 항목 추가'}
              </button>
            )}

            {patrolItems.filter(i => i.is_active).length > 0 && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-gray-500 mb-2">출석부 미리보기:</p>
                <div className="flex justify-center gap-2">
                  {patrolItems.filter(i => i.is_active).map(item => (
                    <div key={item.id} className="flex flex-col items-center p-1.5 rounded border bg-white min-w-[48px]">
                      <span className="text-lg">{item.emoji}</span>
                      <span className="text-xs">{item.name}</span>
                      {item.points > 0 && (
                        <span className={`text-xs ${item.point_type === 'merit' ? 'text-green-600' : 'text-red-600'}`}>
                          {item.point_type === 'merit' ? '+' : '-'}{item.points}점
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 임계값 관리 */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-medium text-gray-900">경고 임계값 설정</h3>
          <button
            onClick={() => setNewThreshold({ name: '', points: 5, color: 'yellow' })}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            임계값 추가
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-gray-500 mb-4">
            학생의 누적 벌점이 설정된 점수에 도달하면 해당 경고 상태가 표시됩니다.
          </p>

          <div className="space-y-3">
            {/* 새 임계값 입력 */}
            {newThreshold && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <input
                  value={newThreshold.name || ''}
                  onChange={(e) => setNewThreshold({ ...newThreshold, name: e.target.value })}
                  placeholder="경고명 (예: 주의)"
                  className="border rounded px-3 py-2 text-sm flex-1"
                />
                <input
                  type="number"
                  value={newThreshold.points || 5}
                  onChange={(e) =>
                    setNewThreshold({ ...newThreshold, points: parseInt(e.target.value) || 5 })
                  }
                  placeholder="점수"
                  className="w-20 border rounded px-3 py-2 text-sm text-center"
                  min={1}
                />
                <span className="text-sm text-gray-500">점 이상</span>
                <select
                  value={newThreshold.color || 'yellow'}
                  onChange={(e) => setNewThreshold({ ...newThreshold, color: e.target.value })}
                  className="border rounded px-3 py-2 text-sm"
                >
                  {THRESHOLD_COLOR_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleSaveThreshold}
                  className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  저장
                </button>
                <button
                  onClick={() => setNewThreshold(null)}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm"
                >
                  취소
                </button>
              </div>
            )}

            {thresholds.map((threshold) => (
              <div
                key={threshold.id}
                className={`flex items-center gap-3 p-3 border rounded-lg ${
                  !threshold.is_active ? 'opacity-50' : ''
                }`}
              >
                {editingThreshold?.id === threshold.id ? (
                  <>
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: THRESHOLD_COLORS[editingThreshold.color] }}
                    ></div>
                    <input
                      value={editingThreshold.name}
                      onChange={(e) =>
                        setEditingThreshold({ ...editingThreshold, name: e.target.value })
                      }
                      className="border rounded px-3 py-2 text-sm flex-1"
                    />
                    <input
                      type="number"
                      value={editingThreshold.points}
                      onChange={(e) =>
                        setEditingThreshold({
                          ...editingThreshold,
                          points: parseInt(e.target.value) || 1,
                        })
                      }
                      className="w-20 border rounded px-3 py-2 text-sm text-center"
                      min={1}
                    />
                    <span className="text-sm text-gray-500">점 이상</span>
                    <select
                      value={editingThreshold.color}
                      onChange={(e) =>
                        setEditingThreshold({ ...editingThreshold, color: e.target.value })
                      }
                      className="border rounded px-3 py-2 text-sm"
                    >
                      {THRESHOLD_COLOR_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleSaveThreshold}
                      className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                    >
                      저장
                    </button>
                    <button
                      onClick={() => setEditingThreshold(null)}
                      className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm"
                    >
                      취소
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: THRESHOLD_COLORS[threshold.color] }}
                    ></div>
                    <span className="font-medium flex-1">{threshold.name}</span>
                    <span className="text-gray-600">{threshold.points}점 이상</span>
                    <button
                      onClick={() => setEditingThreshold(threshold)}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      수정
                    </button>
                    <button
                      onClick={() => onDeleteThreshold(threshold.id)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      삭제
                    </button>
                  </>
                )}
              </div>
            ))}

            {thresholds.length === 0 && !newThreshold && (
              <p className="text-center text-gray-500 py-4">설정된 임계값이 없습니다.</p>
            )}
          </div>
        </div>
      </div>

      {/* 정책 설정 */}
      {setting && (
        <div className="bg-white rounded-lg border">
          <div className="px-4 py-3 border-b">
            <h3 className="font-medium text-gray-900">벌점 정책 설정</h3>
          </div>

          <div className="p-4 space-y-4">
            {/* 자동 부과 설정 */}
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">자동 벌점 부과</div>
                <div className="text-sm text-gray-500">
                  지각/결석 시 자동으로 벌점을 부과합니다.
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={setting.auto_penalty_enabled}
                  onChange={(e) => onUpdateSetting({ auto_penalty_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* 사유서 감면 점수 */}
            {setting.auto_penalty_enabled && (
              <div className="pt-4 border-t">
                <div className="font-medium">사유서 감면 점수</div>
                <div className="text-sm text-gray-500 mb-3">
                  사유서가 승인되면 아래 점수로 부과됩니다. (무단 &gt; 사유 &gt; 미부과 0점)
                  <br />
                  무단결석은 위 '결석' 유형 점수, '벌점 미부과'로 승인하면 0점입니다.
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {([
                    { key: 'excused_absence_points', label: '사유결석', def: 5 },
                    { key: 'excused_late_points', label: '사유지각', def: 3 },
                    { key: 'excused_early_points', label: '사유조퇴', def: 3 },
                  ] as const).map(({ key, label, def }) => (
                    <label key={key} className="flex flex-col gap-1">
                      <span className="text-sm text-gray-700">{label}</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={(setting as any)[key] ?? def}
                          onChange={(e) => {
                            const v = parseInt(e.target.value);
                            onUpdateSetting({ [key]: isNaN(v) ? 0 : Math.max(0, v) } as PenaltySettingUpdate);
                          }}
                          className="w-20 border rounded-lg px-3 py-2 text-center"
                        />
                        <span className="text-gray-500 text-sm">점</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 쉬는시간 지각 벌점 (2026-07-29) — 설정은 operational_rules 에 저장 */}
            {setting.auto_penalty_enabled && <LateReturnSettings />}

            {/* 외출 미복귀 벌점 (2026-08-11) — 설정은 operational_rules 에 저장 */}
            {setting.auto_penalty_enabled && <OutingOverdueSettings />}

            {/* 초기화 주기 */}
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">벌점 초기화 주기</div>
                <div className="text-sm text-gray-500">
                  설정된 주기에 따라 벌점이 초기화됩니다.
                </div>
              </div>
              <select
                value={setting.reset_period}
                onChange={(e) =>
                  onUpdateSetting({
                    reset_period: e.target.value as 'monthly' | 'semester' | 'manual',
                  })
                }
                className="border rounded-lg px-3 py-2"
              >
                {RESET_PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 초기화 일 (월별인 경우) */}
            {setting.reset_period === 'monthly' && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">초기화 일</div>
                  <div className="text-sm text-gray-500">매월 이 날짜에 벌점이 초기화됩니다.</div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={setting.reset_day}
                    onChange={(e) =>
                      onUpdateSetting({ reset_day: parseInt(e.target.value) || 1 })
                    }
                    min={1}
                    max={28}
                    className="w-20 border rounded-lg px-3 py-2 text-center"
                  />
                  <span className="text-gray-500">일</span>
                </div>
              </div>
            )}

            {/* 수동 초기화 */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-red-600">벌점 수동 초기화</div>
                  <div className="text-sm text-gray-500">
                    모든 학생의 현재 벌점을 0으로 초기화합니다.
                  </div>
                </div>
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  벌점 초기화
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-red-600 mb-4">벌점 초기화 확인</h3>
            <p className="text-gray-600 mb-4">
              모든 학생의 현재 벌점이 0으로 초기화됩니다.
              <br />이 작업은 되돌릴 수 없습니다.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                초기화 실행
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
